import { sectionLabels } from "../section-labels";
import { colorFields } from "./_shared";
/**
 * Search prompt — a contained, rounded card carrying one line of copy and a
 * button that opens the store's own search. Ported from the EasyOrders
 * `hs-search` block found on the `blast` family (2026-07-20).
 *
 * A NEW type rather than a variant, and the checks made first:
 *
 *  - `banner` / `banner-promo` is the closest existing shape (a contained
 *    rounded panel with centred copy and a pill CTA), but its CTA is a
 *    DESTINATION — an `href` into the catalogue. This block's button is an
 *    ACTION: it opens the search overlay. That is the only block in the
 *    vocabulary whose call to action is not a link, and bolting a
 *    "ctaHref: search" sentinel onto `banner` would leak a special case into
 *    every other banner variant.
 *  - `banner` is also image-LED (the artwork is the panel, the copy sits over
 *    it). This block is colour-led: the ground is a flat fill and the artwork
 *    is an optional decorative pattern washed back behind it. MEASURED on
 *    blast-books, the panel is 188px at 1440 against `banner-promo`'s 332 —
 *    a different box driven by different rules.
 *
 * Every value in the renderer is transcribed from their inline stylesheet,
 * which ships on the page, rather than approximated:
 *
 *   card    radius 20px (<768) / 32px (>=768) · overflow hidden · isolation
 *   ground  flat fill, authored `--hs-search-bg: #fff4b9`
 *   pattern absolute inset 0 · cover/center · opacity .65 (<768) / .64 above
 *   inner   flex column · centred · gap 16px
 *           padding `20px 24px` (<768) / `48px 32px` (>=768)
 *   title   20px/w900 lh 1.2 max-width 22rem (<768)
 *           24px/w900 lh 1.15 no max-width (>=768)
 *   button  pill radius 32 · `11px 24px` (<768) / `14px 24px` (>=768)
 *           14px/w700 · lh 1 · icon + label, gap 8
 *
 * Their literal `#fff4b9` ground / `#0F1A3C` button are NOT ported as hex —
 * they become band tokens through `surface`, for the reason every other port
 * records: a theme that hardcodes one family's ground ignores the store's
 * palette the moment a seller changes it.
 */
export const searchPromptSchema = {
    type: "search-prompt",
    name: sectionLabels["search-prompt"],
    description: {
        ar: "كارت بسؤال وزرار بيفتح البحث في المتجر.",
        en: "A card with one line of copy and a button that opens store search.",
    },
    maxPerTemplate: 1,
    availableOnTemplates: ["home"],
    // Furniture, not a menu item: 1 preset (`blast`), 1 demo instance. A card
    // that says "didn't find it? search" only makes sense in a look whose header
    // already leads with search; offered on its own it asks the seller to model
    // a recovery flow nobody described to them.
    offeredInAddMenu: false,
    settings: [
        {
            id: "title",
            type: "text",
            i18n: true,
            label: { ar: "السؤال", en: "Prompt" },
            helpText: {
                ar: "سطر واحد بيشجع الزائر إنه يدوّر.",
                en: "One line inviting the shopper to search.",
            },
            validate: { max: 80 },
            default: {
                ar: "مش لاقي المنتج اللي بتدور عليه؟",
                en: "Didn't find the product you were looking for?",
            },
        },
        {
            id: "buttonLabel",
            type: "text",
            i18n: true,
            label: { ar: "نص الزرار", en: "Button label" },
            validate: { max: 24 },
            default: { ar: "ابحث", en: "Search" },
        },
        {
            id: "headingLevel",
            type: "headingLevel",
            label: { ar: "مستوى العنوان", en: "Heading level" },
            default: 2,
        },
        {
            /**
             * The CARD's ground.
             *
             * Deliberately NOT the shared `surface` field that `colorFields()`
             * contributes below: that one paints the SECTION band, and this block's
             * section band is the page — the coloured thing is the contained card
             * sitting on it. Same five slot names, so "which band" stays one
             * vocabulary rather than two.
             *
             * Their block authors a literal pale yellow `#fff4b9`; ours takes a band
             * so the card follows the store's palette. `alt` is the default because
             * that is what their instance reads as against a white page — a soft
             * panel that separates from the ground without shouting.
             */
            id: "cardSurface",
            type: "select",
            label: { ar: "لون الكارت", en: "Card background" },
            options: [
                { value: "page", label: { ar: "زي الصفحة", en: "Same as page" } },
                { value: "alt", label: { ar: "فاتح مختلف", en: "Subtle" } },
                { value: "primary", label: { ar: "اللون الأساسي", en: "Primary" } },
                { value: "accent", label: { ar: "اللون المميّز", en: "Accent" } },
                { value: "ink", label: { ar: "غامق", en: "Dark" } },
            ],
            default: "alt",
        },
        {
            id: "image",
            type: "image",
            label: { ar: "نقشة الخلفية (اختياري)", en: "Background pattern (optional)" },
            helpText: {
                ar: "صورة بتتحط ورا الكارت بشفافية. سيبها فاضية لو عايز لون سادة.",
                en: "A decorative image washed back behind the card. Leave empty for a flat fill.",
            },
            focalPoint: true,
        },
        {
            /**
             * How strongly the pattern reads. Their two authored stops are .65
             * (<768) and .64 (>=768) — effectively one value, so this is exposed as
             * a single control defaulting to their 65 rather than two breakpoints.
             */
            id: "patternOpacity",
            type: "range",
            label: { ar: "وضوح النقشة", en: "Pattern strength" },
            helpText: {
                ar: "أعلى = النقشة أوضح ورا الكلام.",
                en: "Higher shows more of the pattern behind the copy.",
            },
            min: 0,
            max: 100,
            step: 5,
            default: 65,
        },
        ...colorFields(),
    ],
};
//# sourceMappingURL=search-prompt.js.map